

import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { SUBSCRIPTION_PLAN_TYPE } from "@/lib/api/types";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { useSubscriptionsQuery } from "@/shared/services/useSubscriptionsQuery";
import { SubscriptionPlanCreateModal } from "../components/SubscriptionPlanCreateModal";
import { SubscriptionPlanCard } from "../components/SubscriptionPlanCard";
import { Button, EmptyState, LoadingSkeleton, ErrorState } from "@a2z/ui";

type AdminSubscriptionsPageClientProps = {
  locale: string;
  searchParams: {
    active?: string;
    inactive?: string;
    paid?: string;
    free?: string;
  };
};

function hasFlag(value?: string) {
  return value === "true";
}

export function SubscriptionsPage({
  locale,
  searchParams,
}: AdminSubscriptionsPageClientProps) {
  const t = useTranslations();
  const { data: session, isLoading: isSessionLoading } = useSessionQuery();
  const { data, isLoading, error, refetch } = useSubscriptionsQuery();

  if (isSessionLoading || isLoading) {
    return <LoadingSkeleton variant="list" rows={4} />;
  }

  if (error || !data) {
    return <ErrorState onRetry={refetch} />;
  }

  const elevatedAdminRoles = [ADMIN_ROLES.ADMIN, ADMIN_ROLES.SUPER_ADMIN];
  const canManagePlans =
    session?.admin?.roles?.some((role) =>
      elevatedAdminRoles.includes(role as (typeof elevatedAdminRoles)[number]),
    ) ?? false;
  const showInactiveFilter = canManagePlans;

  const filterActive = hasFlag(searchParams.active);
  const filterInactive = hasFlag(searchParams.inactive);
  const filterPaid = hasFlag(searchParams.paid);
  const filterFree = hasFlag(searchParams.free);

  const statusFiltered = filterActive || filterInactive;
  const typeFiltered = filterPaid || filterFree;

  const filteredPlans = data.items.filter((plan) => {
    if (!canManagePlans && !plan.isActive) {
      return false;
    }
    if (statusFiltered) {
      if (plan.isActive && !filterActive) {
        return false;
      }
      if (!plan.isActive && !filterInactive) {
        return false;
      }
    }
    if (typeFiltered) {
      if (plan.type === SUBSCRIPTION_PLAN_TYPE.MONTHLY_SUBSCRIPTION && !filterPaid) {
        return false;
      }
      if (plan.type === SUBSCRIPTION_PLAN_TYPE.FREE_TRIAL && !filterFree) {
        return false;
      }
    }
    return true;
  });

  return (
    <section className="flex w-full max-w-4xl flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[23px] font-bold leading-tight tracking-[-0.025em] lg:text-[28px] lg:font-semibold lg:tracking-[-0.01em] text-[color:var(--text-primary)]">
            {t(keys.admin.subscriptionsTitle)}
          </h1>
        </div>
      </div>

      {canManagePlans ? (
        <div className="rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-5 py-4 shadow-[var(--shadow-sm)]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-[color:var(--text-secondary)]">
                {t(keys.admin.subscriptionCreateTitle)}
              </div>
              <p className="mt-1 text-xs text-[color:var(--text-muted)]">
                {t(keys.admin.subscriptionCreateHint)}
              </p>
            </div>
            <SubscriptionPlanCreateModal />
          </div>
        </div>
      ) : null}

      <form
        action={`/${locale}/admin/subscriptions`}
        method="get"
        className="flex flex-wrap items-center gap-3 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 text-sm"
      >
        <label className="flex items-center gap-2 text-xs text-[color:var(--text-secondary)]">
          <input
            type="checkbox"
            name="active"
            value="true"
            defaultChecked={filterActive}
            className="h-4 w-4"
          />
          {t(keys.admin.subscriptionFilterActive)}
        </label>
        {showInactiveFilter ? (
          <label className="flex items-center gap-2 text-xs text-[color:var(--text-secondary)]">
            <input
              type="checkbox"
              name="inactive"
              value="true"
              defaultChecked={filterInactive}
              className="h-4 w-4"
            />
            {t(keys.admin.subscriptionFilterInactive)}
          </label>
        ) : null}
        <label className="flex items-center gap-2 text-xs text-[color:var(--text-secondary)]">
          <input
            type="checkbox"
            name="paid"
            value="true"
            defaultChecked={filterPaid}
            className="h-4 w-4"
          />
          {t(keys.admin.subscriptionFilterPaid)}
        </label>
        <label className="flex items-center gap-2 text-xs text-[color:var(--text-secondary)]">
          <input
            type="checkbox"
            name="free"
            value="true"
            defaultChecked={filterFree}
            className="h-4 w-4"
          />
          {t(keys.admin.subscriptionFilterFree)}
        </label>
        <Button
          type="submit"
          variant="outline"
          className="h-9 rounded-full px-3 text-xs font-semibold transition max-lg:h-10"
        >
          {t(keys.common.applyFilters)}
        </Button>
        <a
          href={`/${locale}/admin/subscriptions`}
          className="text-xs font-semibold text-[color:var(--text-muted)]"
        >
          {t(keys.common.clearFilters)}
        </a>
      </form>

      {filteredPlans.length === 0 ? (
        <EmptyState
          title={t(keys.common.emptyTitle)}
          description={t(keys.admin.subscriptionsEmptySubtitle)}
        />
      ) : (
        <div className="grid gap-3">
          {filteredPlans.map((plan) => (
            <SubscriptionPlanCard
              key={plan.id}
              plan={plan}
              canEdit={!!canManagePlans}
            />
          ))}
        </div>
      )}
    </section>
  );
}
